import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { FaCircleCheck, FaShieldHalved, FaCircleQuestion, FaIdCard, FaPassport, FaPhone, FaClipboardList } from "react-icons/fa6";
import { FaRegClock, FaRegEdit } from "react-icons/fa";
import "./ApplyBeforeYouBegin.css";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = (d = 0) => ({ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease, delay: d } } });

const ITEMS = [
  { Icon: FaIdCard, label: "Government ID", sub: "Valid photo ID" },
  { Icon: FaPassport, label: "Passport (if applicable)", sub: "For travel outside the U.S." },
  { Icon: FaClipboardList, label: "Service Verification", sub: "DD-214 or equivalent" },
  { Icon: FaRegClock, label: "Availability", sub: "20–30 minutes to complete" },
  { Icon: FaPhone, label: "Emergency Contact", sub: "Name and phone number" },
  { Icon: FaRegEdit, label: "Why You're Applying", sub: "Be ready to share your story" },
];

const ELQ = ["Are you currently experiencing active suicidal thoughts?", "Are you currently experiencing active addiction or substance use that would make participation unsafe?"];

export default function ApplyBeforeYouBegin() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [answers, setAnswers] = useState({ 0: null, 1: null });

  const answer = (qi, val) => setAnswers((prev) => ({ ...prev, [qi]: val }));
  const crisis = Object.values(answers).some((v) => v === true);

  return (
    <section className="abyb position-relative" ref={ref}>
      <img src="./assets/plant.png" alt="" className="abyb__plant" aria-hidden="true" />
      <div className="abyb__layout container-fg container-2xxl">
        {/* LEFT */}
        <div className="abyb__left">
          <motion.div className="abyb__left-text" variants={fadeUp(0)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <p className="abyb__eyebrow u-eyebrow">Before You Begin</p>
            <h2 className="abyb__headline u-display">
              A Few Things
              <br />
              To Have Ready
            </h2>
            <p className="abyb__sub">Gather the following before starting your application.</p>
          </motion.div>

          <div className="abyb__grid">
            {ITEMS.map((item, i) => (
              <motion.div key={item.label} className="abyb__item" variants={fadeUp(0.06 + i * 0.05)} initial="hidden" animate={inView ? "show" : "hidden"}>
                <FaCircleCheck className="abyb__check" aria-hidden="true" />
                <div>
                  <p className="abyb__item-label">{item.label}</p>
                  <p className="abyb__item-sub">{item.sub}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* RIGHT — eligibility widget */}
        <motion.div className="abyb__elig" variants={fadeUp(0.1)} initial="hidden" animate={inView ? "show" : "hidden"}>
          <div className="abyb__elig-head">
            <FaShieldHalved className="abyb__elig-shield" aria-hidden="true" />
            <h3 className="abyb__elig-title">Early Eligibility Check</h3>
          </div>
          <p className="abyb__elig-intro">To ensure this program is the right fit, we ask two important questions at the start of the application.</p>

          {ELQ.map((q, i) => (
            <div key={i} className="abyb__q">
              <div className="abyb__q-row">
                <FaCircleQuestion className="abyb__q-icon" aria-hidden="true" />
                <p className="abyb__q-text">{q}</p>
              </div>
              <div className="abyb__q-btns">
                <button type="button" className={`abyb__q-btn ${answers[i] === true ? "active-yes" : ""}`} onClick={() => answer(i, true)}>
                  YES
                </button>
                <button type="button" className={`abyb__q-btn ${answers[i] === false ? "active-no" : ""}`} onClick={() => answer(i, false)}>
                  NO
                </button>
              </div>
            </div>
          ))}

          <div className={`abyb__note ${crisis ? "abyb__note--crisis" : ""}`}>
            <FaShieldHalved aria-hidden="true" />
            <p>
              {crisis
                ? "We will share immediate resources and a member of our team will reach out to you. Please call or text 988 (Suicide & Crisis Lifeline) right now."
                : "If you answer yes to either question, we will share immediate resources and a member of our team will reach out to you."}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
